import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { sessionLogout } from '../../slices/sessionSlice';
import classnames from 'classnames';
import cookies from '../../utils/cookies';
import { ReactComponent as HomeIcon } from '../../svg/home.svg';
import { ReactComponent as BookIcon } from '../../svg/book.svg';
import { ReactComponent as TrendingIcon } from '../../svg/trending-up.svg';
import { ReactComponent as UserIcon } from '../../svg/user.svg';
import { ReactComponent as UsersIcon } from '../../svg/users.svg';
import { ReactComponent as PassportLogo } from '../../svg/passport_only.svg';
import { ReactComponent as BrandMark } from '../../svg/ico-passport.svg';
import { ReactComponent as CogIcon } from '../../svg/cog.svg';
import { ReactComponent as FAQIcon } from '../../svg/faq.svg';
import { ReactComponent as MinusIcon } from '../../svg/minus.svg';
import { ReactComponent as LogoutIcon } from '../../svg/log-out.svg';
import { ReactComponent as ChevronLeft } from '../../svg/chevron-left.svg';
import { ReactComponent as BarChart } from '../../svg/bar-chart.svg';
import { useHistory } from 'react-router-dom';
import useFeatureFlags from '../../hooks/useFeatureFlags';

const SidebarNav = ({
    collapseSidebar = false,
    user = {},
    hintAt = false,
    toggleSidebar = () => {},
    toggleVendorChangeModal = () => {}
}) => {
    const SUPPORT_EMAIL = 'pub-support@inaudio.com';
    const session = useSelector((state) => state.session);
    const dispatch = useDispatch();
    const history = useHistory();
    const handleLogout = (event) => {
        event.preventDefault();
        dispatch(sessionLogout());
        cookies.remove('auth_token', { path: '/' });
        cookies.remove('refresh_token', { path: '/' });
        cookies.remove('access_key', { path: '/' });
        history.push('/login');
    };
    const featureFlags = useFeatureFlags();

    const makeAccountTooltip = () => {
        if (session?.user?.vendors?.length > 1) {
            if (collapseSidebar) {
                return `Your Account - You are using the site as ${session.selectedVendorName}`;
            } else {
                return `You are using the site as ${session.selectedVendorName}`;
            }
        }

        if (collapseSidebar) {
            return 'Your Account';
        }

        return null;
    };

    return (
        <nav
            className={classnames('sidebar', {
                'sidebar-slim': collapseSidebar
            })}
        >
            <div className="sidebar-wrapper">
                <Link className="logo" to="/">
                    {!collapseSidebar && <PassportLogo className="logo-full" width="175" fill="#fff" />}
                    {collapseSidebar && <BrandMark className="logo-small" width="48" fill="#fff" />}
                </Link>

                <ul className="nav flex-column">
                    <li className="nav-item">
                        <NavLink
                            to="/home"
                            exact
                            className="nav-link"
                            data-rh={collapseSidebar ? 'Home' : null}
                            data-rh-at={hintAt}
                            activeClassName="active"
                        >
                            <HomeIcon aria-hidden /> <span>Home</span>
                        </NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink
                            to="/catalog"
                            className="nav-link"
                            data-rh={collapseSidebar ? 'Catalog' : null}
                            data-rh-at={hintAt}
                            activeClassName="active"
                        >
                            <BookIcon aria-hidden /> <span>Catalog</span>
                        </NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink
                            to="/reporting"
                            className="nav-link"
                            activeClassName="active"
                            data-rh={collapseSidebar ? 'Royalty Reporting' : null}
                            data-rh-at={hintAt}
                        >
                            <TrendingIcon aria-hidden /> <span>Royalty Reporting</span>
                        </NavLink>
                        <ul className="nav">
                            <li className="nav-item">
                                <NavLink to="/reporting/sales" className="nav-link">
                                    <MinusIcon aria-hidden /> <span>Unofficial Sales</span>
                                </NavLink>
                            </li>
                        </ul>
                    </li>
                        <li className="nav-item">
                            <NavLink
                            to="/account"
                            className="nav-link"
                            activeClassName="active"
                            data-rh={makeAccountTooltip()}
                            data-rh-at={hintAt}
                        >
                            <CogIcon aria-hidden /> <span>Account </span>
                            {session?.user?.vendors?.length > 1 && (
                                <span className="badge badge-light-blue">{session?.selectedVendorName}</span>
                            )}
                        </NavLink>
                        <ul className="nav">
                            <li className="nav-item">
                                <NavLink to="/account/change-password" className="nav-link">
                                    <MinusIcon aria-hidden /> <span>Change Password</span>
                                </NavLink>
                            </li>
                            {session.user?.provider?.vendors?.length > 1 && (
                                <li className="nav-item">
                                    <button className="nav-link btn-link" onClick={toggleVendorChangeModal}>
                                        Change Account
                                    </button>
                                </li>
                            )}
                        </ul>
                    </li>

                    {session.isAdmin && (
                        <>
                            <hr />
                            <li className="nav-item">
                                <NavLink
                                    to="/users"
                                    className="nav-link"
                                    data-rh={collapseSidebar ? 'Users' : null}
                                    data-rh-at={hintAt}
                                >
                                    <UserIcon /> <span>Users</span>
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    to="/providers"
                                    className="nav-link"
                                    data-rh={collapseSidebar ? 'Providers' : null}
                                    data-rh-at={hintAt}
                                >
                                    <UsersIcon /> <span>Providers</span>
                                </NavLink>
                            </li>
                        </>
                    )}

                    <hr />

                    <li className="nav-item">
                        <a
                            className="nav-link"
                            href={`mailto:${SUPPORT_EMAIL}`}
                            data-rh={collapseSidebar ? 'Help' : null}
                            data-rh-at={hintAt}
                        >
                            <FAQIcon /> <span>Help</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a
                            className="nav-link"
                            href="#logout"
                            onClick={handleLogout}
                            data-rh={collapseSidebar ? 'Logout' : null}
                            data-rh-at={hintAt}
                        >
                            <LogoutIcon /> <span>Log out</span>
                        </a>
                    </li>
                </ul>
                <button className="btn collapse-button" onClick={toggleSidebar}>
                    <ChevronLeft />
                </button>
            </div>
        </nav>
    );
};
export default SidebarNav;
